import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {updateIssueIssueTypeSharedMutation as updateIssueIssueTypeQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueIssueTypeSharedMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueIssueTypeSharedMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

export type OptimisticIssueType = IssueTypePickerIssueType

type UpdateIssueIssueTypeCallbacks = {
  onError?: (error: Error) => void
  onCompleted?: (response: UpdateIssueIssueTypeSharedMutationMutation) => void
}

export type UpdateIssueIssueType = (
  issueId: string,
  issueType: OptimisticIssueType | null,
  callbacks?: UpdateIssueIssueTypeCallbacks,
) => void

function issueTypeOperation(issueId: string, issueType: OptimisticIssueType | null): SetFieldsOperation {
  return {type: 'setFields', id: issueId, fields: {issueType}}
}

async function updateIssueIssueTypeRequest({
  owner,
  repo,
  issueNumber,
  issueId,
  issueTypeId,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  issueTypeId: string | null
  clientMutationId?: string
}): Promise<UpdateIssueIssueTypeSharedMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueIssueTypeQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/issue_type.json?${query}`,
    {method: 'PATCH', body: {issueTypeId, clientMutationId}},
  )

  let result:
    | {
        data?: UpdateIssueIssueTypeSharedMutationMutation | null
        errors?: ReadonlyArray<{message?: string}> | null
      }
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.updateIssueIssueType?.issue
  const assignmentMismatch = issueTypeId ? issue?.issueType?.id !== issueTypeId : issue?.issueType !== null
  if (!response.ok || !data || !issue || issue.id !== issueId || assignmentMismatch) {
    throw new Error(error ?? 'Failed to update issue type')
  }
  return data
}

async function notifyMutationResult(
  mutation: Promise<UpdateIssueIssueTypeSharedMutationMutation>,
  callbacks?: UpdateIssueIssueTypeCallbacks,
): Promise<void> {
  let response: UpdateIssueIssueTypeSharedMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error('Failed to update issue type'))
    return
  }
  callbacks?.onCompleted?.(response)
}

export function useUpdateIssueIssueTypeMutation({
  owner,
  repo,
  issueNumber,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  clientMutationId?: string
}): UpdateIssueIssueType {
  const {mutateAsync} = useEntityMutation<UpdateIssueIssueTypeSharedMutationMutation>({
    scope: {id: `update-issue-type:${owner}/${repo}#${issueNumber}`},
    mutationFn: operation => {
      const issueType = operation.fields.issueType as OptimisticIssueType | null
      return updateIssueIssueTypeRequest({
        owner,
        repo,
        issueNumber,
        issueId: operation.id,
        issueTypeId: issueType?.id ?? null,
        clientMutationId,
      })
    },
    getConfirmedOperation: data => {
      const issue = data.updateIssueIssueType?.issue
      return issue ? issueTypeOperation(issue.id, issue.issueType) : undefined
    },
  })

  return useCallback(
    (issueId, issueType, callbacks) => {
      void notifyMutationResult(mutateAsync({operation: issueTypeOperation(issueId, issueType)}), callbacks)
    },
    [mutateAsync],
  )
}
